import { css } from '@emotion/react';
import { ObjectType } from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { buildAuditFilter, useSearchAuditEvents } from '@frontend/api-phone-audits';
import { Missing } from '@frontend/empty-states#svg';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { useMatchMedia } from '@frontend/responsiveness';
import { useSettingsNavigate } from '@frontend/settings-routing';
import {
  Button,
  ModalControlResponse,
  SpinningLoader,
  Text,
  Tray,
  useAlert,
  WIDTH_DIMS,
} from '@frontend/design-system';
import { logError } from '../../../utils/loggers';
import { EventDetailsList } from './event-list/event-details-list';

const DESIGN_WIDTH: keyof typeof WIDTH_DIMS = 'smallXL';
export const HISTORY_TRAY_WIDTH = WIDTH_DIMS[DESIGN_WIDTH] ?? 400;

// Below this screen width (in pixels), squishing the page content next to the tray leaves the left content too thin,
// so we need the tray to overlap the content instead of squishing it.
export const HISTORY_TRAY_OVERLAP_MAX_WIDTH = 760;

// Returns true when the screen is too narrow to squish the page content, meaning the tray should overlap instead.
export const useHistoryTrayShouldOverlap = () => useMatchMedia({ maxWidth: HISTORY_TRAY_OVERLAP_MAX_WIDTH });

type HistoryDetailsTrayProps = {
  HeaderComponent?: React.ReactNode;
  tenantId: string;
  callObjectId?: string;
  callObjectType?: ObjectType;
  auditEventId?: string;
  modalControls: ModalControlResponse;
  mountTarget?: string;
  // Controls react-query's refetchOnMount behavior for the tray's audit event data. Defaults to 'always' so that
  // every time the tray is opened it fetches the latest data. Pages that manage their own cache invalidation (e.g.
  // clearing the audit event cache when the page mounts) can pass `true` instead so that reopening the tray for the
  // same object reuses the cache rather than always refetching.
  refetchOnMount?: boolean | 'always';
  // Optional upper bound (ISO timestamp) for the tray's audit event history, i.e. "don't show/fetch events created
  // after this time".
  historyCutoffTime?: string;
  showAllHistoryButton?: boolean;
};

// The HistoryDetailsTray is a right-side tray that displays the history of a phone system object (call blocking,
// hold music, etc).
// HistoryDetailsTray renders the Tray component that wraps the HistoryDetailsTrayContent component so that the data
// fetching and any other logic is not executed unless the tray is open,
export const HistoryDetailsTray: React.FC<HistoryDetailsTrayProps> = ({
  HeaderComponent,
  tenantId,
  callObjectId,
  callObjectType,
  auditEventId,
  modalControls,
  mountTarget = '[data-settings-modal-content]',
  refetchOnMount = 'always',
  historyCutoffTime,
  showAllHistoryButton,
}) => {
  // On narrow screens the tray overlaps the page content instead of squishing it, so show a backdrop to focus the tray.
  const shouldOverlap = useHistoryTrayShouldOverlap();

  return (
    <Tray
      mountTarget={mountTarget}
      {...modalControls.modalProps}
      showBackdrop={shouldOverlap}
      // IMPORTANT: the width of the tray must match the HISTORY_TRAY_WIDTH constant above which is used to calculate
      // the width of the different settings pages when the tray is open. If changing the width here, make sure to
      // update the HISTORY_TRAY_WIDTH constant above.
      width={DESIGN_WIDTH}
      css={trayStyle}
    >
      <HistoryDetailsTrayContent
        HeaderComponent={HeaderComponent}
        tenantId={tenantId}
        callObjectId={callObjectId}
        callObjectType={callObjectType}
        auditEventId={auditEventId}
        refetchOnMount={refetchOnMount}
        historyCutoffTime={historyCutoffTime}
        showAllHistoryButton={showAllHistoryButton}
      />
    </Tray>
  );
};

const HistoryDetailsTrayContent = ({
  HeaderComponent,
  tenantId,
  callObjectId,
  callObjectType,
  auditEventId,
  refetchOnMount,
  historyCutoffTime,
  showAllHistoryButton = true,
}: {
  HeaderComponent?: React.ReactNode;
  tenantId: string;
  callObjectId?: string;
  callObjectType?: ObjectType;
  auditEventId?: string;
  refetchOnMount: boolean | 'always';
  historyCutoffTime?: string;
  showAllHistoryButton?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const { data, isLoading } = useSearchAuditEvents(
    {
      tenantId,
      filter:
        buildAuditFilter({
          objectId: callObjectId,
          objectTypes: callObjectType ? [callObjectType] : undefined,
          endTime: historyCutoffTime,
        }) || undefined,
    },
    { baseUrl: appConfig.BACKEND_API_V2 },
    {
      retry: false,
      // Data is never considered stale on its own; when refetchOnMount is 'always' a fresh fetch happens regardless,
      // and when it's true/false the cache is reused for as long as it exists (callers control invalidation).
      staleTime: Infinity,
      refetchOnMount,
      enabled: (!!callObjectId || !!callObjectType) && !!tenantId,
      onError(error) {
        alerts.error({
          message: t('Failed to fetch phone system history details'),
        });
        logError('HistoryDetailsTray:useSearchAuditEvents | Failed to fetch phone system history', {
          data: {
            tenantId,
            callObjectId,
            callObjectType,
            error,
          },
        });
      },
    }
  );

  const auditEvents = data?.auditEvents ?? [];

  const isEmpty = isLoading || auditEvents.length === 0;

  return (
    <section css={traySectionStyle}>
      {HeaderComponent}
      <div css={trayContentStyle(isEmpty)}>
        {isLoading ? (
          <SpinningLoader />
        ) : auditEvents.length > 0 ? (
          <EventDetailsList events={auditEvents} activeEventId={auditEventId} />
        ) : (
          <>
            <Missing />
            <Text color='light'>{t('No history yet. New changes will appear here.')}</Text>
          </>
        )}
      </div>

      <div css={trayFooterStyle}>
        {callObjectId && (
          <Text size='small' color='subdued'>
            {t('Object ID: {{id}}', { id: callObjectId })}
          </Text>
        )}
        {showAllHistoryButton && (
          <Button
            iconName='history-small'
            variant='tertiary'
            onClick={() => settingsNavigate({ to: '/phone/phone-system-history' })}
          >
            {t('See all Phone System History')}
          </Button>
        )}
      </div>
    </section>
  );
};

// --- Styles ---

const trayStyle = css`
  padding: var(--fabric-space-gap-md);
`;

const traySectionStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const trayContentStyle = (isEmpty: boolean) => css`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  ${isEmpty
    ? 'align-items: center; justify-content: flex-start; padding-top: 100px'
    : 'padding-top: var(--fabric-space-gap-md);'}
`;

const trayFooterStyle = css`
  flex-shrink: 0;
  padding-top: var(--fabric-space-gap-sm);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
